import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
} from "react";
import {
    Elements,
    ExpressCheckoutElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "../../context/auth/AuthContext";
import { useStripePayment } from "../../services/hooks/useStripePayment";
import { debounce } from "lodash";
import { useLocation } from "react-router-dom";

type StripePaymentProps = {
    onError: (error: string) => void;
    amount: number;
    additionalMetadata: Record<string, any>;
};

export type StripePaymentHandler = {
    changedValue: (value: number) => void;
};

const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? ""
);
const establishmentId = process.env.REACT_APP_PAYMENT_ESTABLISHMENT_ID ?? '';

const StripePayment = forwardRef<StripePaymentHandler, StripePaymentProps>(
    ({ onError, amount, additionalMetadata }, ref) => {
        const { user } = useAuth();

        const paymentData = useMemo(
            () => ({
                amount,
                metadata: JSON.stringify({
                    meepUserId: user?.id,
                    ...additionalMetadata
                }),
                establishmentId,
                payer: {
                    name: user?.name,
                    document: user?.cpf,
                }
            }),
            [amount, user?.id, user?.name, user?.cpf]
        );

        const { data: responsePayment, refetch: refetchPayment } = useStripePayment(paymentData);

        const clientSecret = responsePayment?.data?.value?.clientSecret;

        const debouncedRefetchPayment = useMemo(
            () => debounce(refetchPayment, 700),
            [refetchPayment]
        );

        useImperativeHandle(ref, () => ({
            changedValue() {
                refetchPayment();
            },
        }));

        useEffect(() => {
            debouncedRefetchPayment();
            return () => debouncedRefetchPayment.cancel();
        }, [paymentData, debouncedRefetchPayment]);

        if (!clientSecret) {
            return <p style={{textAlign: "center"}}>Carregando carteira...</p>;
        }

        return (
            <Elements
                stripe={stripePromise}
                options={{ clientSecret }}
                key={clientSecret}
            >
                <StripeButtons onError={onError} />
            </Elements>
        );
    }
);

type StripeButtonsProps = {
    onError: (error: string) => void;
};

const StripeButtons = ({ onError }: StripeButtonsProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const location = useLocation();

    if (!stripe || !elements) {
        return null;
    }

    return (
        <ExpressCheckoutElement
            onConfirm={async () => {
                if (!stripe || !elements) return;

                const { error } = await stripe.confirmPayment({
                    elements,
                    confirmParams: {
                        return_url: `${window.location.origin}${location.pathname}/pagamento-sucesso`,
                    },
                });

                if (error) {
                    onError(error.message ?? "Ocorreu um erro ao processar o pagamento.");
                }
            }}
            options={{
                wallets: {
                    applePay: "auto",
                    googlePay: "auto",
                },
                buttonType: {
                    applePay: "plain",
                    googlePay: "plain",
                },
                buttonTheme: {
                    applePay: "black",
                    googlePay: "black"
                },
            }}
        />
    );
};

export { StripePayment };
